/**
 * REST client for image-generation job records (`GET /user-files?imageJobs=1`
 * on the async HTTP API). Backs the Jobs panel's Images sub-list.
 *
 * Each record is the C7 `studio/<jobId>/job.json` status file: written by the
 * agents runtime's `generate_image` tool at enqueue (`status: "queued"`) and
 * flipped to `complete`/`failed` by the infra completion consumer. The Lambda
 * aggregates them server-side (newest first, capped) so the panel gets one
 * response instead of fetching per-record presigned URLs (the docs bucket has
 * no CORS config, deliberately). Auth mirrors `userFilesApi.ts`.
 */

import { getAccessToken } from '../auth/accessToken.ts';
import { asyncApiConfig } from '../config/async-api-config.ts';
import { requestTimeout } from '../config/requestTimeout.ts';
import { ApiRequestError } from './apiRequestError.ts';

/** One image-generation job record. Fields beyond `jobId`/`status` are
 *  best-effort — a pre-C7 or consumer-created record may omit them. */
export interface ImageJob {
  readonly jobId: string;
  /** `queued` while generating, then `complete` or `failed`. */
  readonly status: string;
  readonly prompt?: string;
  readonly model?: string;
  readonly createdAt?: string;
  readonly updatedAt?: string;
  /** Delivered filenames (complete only) — under `studio/<jobId>/` in Files. */
  readonly files?: readonly string[];
  readonly error?: string;
  /** ISO instant the delivered images auto-delete (temporary storage). Absent
   *  once kept, on failed jobs, and on pre-expiry records. */
  readonly expiresAt?: string;
  /** True once the user asked the agent to keep the image permanently. */
  readonly kept?: boolean;
  /** Server-side ETA stamped at enqueue (queue depth × median + the model's
   *  last run) — drives the generation countdown. Absent when the studio
   *  couldn't estimate; stale (ignored) once the job is terminal. */
  readonly estimatedSeconds?: number;
  readonly estimatedReadyAt?: string;
  /** Presigned GET for the delivered thumbnail (`thumb.png`), minted
   *  server-side from the record's own key. Absent on jobs delivered before
   *  thumbnails shipped, and on failed/queued jobs. */
  readonly thumbUrl?: string;
}

/** List the signed-in user's image-generation jobs, newest first. */
export async function listImageJobs(): Promise<readonly ImageJob[]> {
  const jwt = await getAccessToken();
  const response = await fetch(`${asyncApiConfig.baseUrl}/user-files?imageJobs=1`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${jwt}` },
    signal: requestTimeout(),
  });
  if (!response.ok) {
    console.error(`[imageJobsApi] list failed (${response.status})`, await safeText(response));
    throw new ApiRequestError('Could not load your image jobs.', `http-${response.status}`);
  }
  const body = (await response.json()) as { jobs?: readonly unknown[] };
  return (body.jobs ?? []).filter(isImageJob);
}

/** Keep only records with the two fields the panel cannot render without. */
function isImageJob(record: unknown): record is ImageJob {
  return (
    typeof record === 'object' &&
    record !== null &&
    typeof (record as { jobId?: unknown }).jobId === 'string' &&
    typeof (record as { status?: unknown }).status === 'string'
  );
}

/** Best-effort body read for an error log — never throws. */
async function safeText(response: Response): Promise<string> {
  try {
    return await response.text();
  } catch {
    return '';
  }
}
